import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { BellRing } from "lucide-react";
import { DateField } from "./DateField";

export function BookingForm({
  cottagePlots,
  form,
  setForm,
  busy,
  blockedDates,
  availability,
  preview,
  previewErr,
  admin,
  onSubmit,
  onOpenWaitlist,
}) {
  return (
    <form onSubmit={onSubmit} className="grid md:grid-cols-5 gap-4 p-6 border border-border rounded-lg bg-card mb-10" data-testid="booking-form">
      <div className="md:col-span-2" data-testid="booking-plot-field">
        <Label>Plot</Label>
        <Select value={form.plot_id} onValueChange={(v) => setForm({ ...form, plot_id: v })}>
          <SelectTrigger data-testid="booking-plot-select"><SelectValue placeholder="Select cottage plot" /></SelectTrigger>
          <SelectContent>
            {cottagePlots.map((p) => (
              <SelectItem key={p.id} value={p.id} data-testid={`booking-plot-option-${p.id}`}>
                Plot {p.plot_number} · {p.project_name || "—"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <DateField label="Check-in" value={form.check_in} onChange={(v) => setForm({ ...form, check_in: v })} blockedDates={blockedDates} testid="booking-checkin-input" />
      <DateField label="Check-out" value={form.check_out} onChange={(v) => setForm({ ...form, check_out: v })} minDate={form.check_in} blockedDates={blockedDates} testid="booking-checkout-input" />
      <div data-testid="booking-guests-field">
        <Label>Guests</Label>
        <Input type="number" min={1} max={20} value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} data-testid="booking-guests-input" />
      </div>
      <div className="md:col-span-4" data-testid="booking-notes-field">
        <Label>Notes</Label>
        <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="booking-notes-input" />
      </div>
      <div className="md:col-span-5" data-testid="booking-guests-emails-field">
        <Label>Guest emails (up to 4) — auto-invited when confirmed</Label>
        <div className="grid grid-cols-2 gap-2 mt-1">
          {[0, 1, 2, 3].map((i) => (
            <Input key={i} type="email" placeholder={`guest${i + 1}@example.com`}
                   value={form.guest_emails[i]}
                   onChange={(e) => {
                     const arr = [...form.guest_emails]; arr[i] = e.target.value;
                     setForm({ ...form, guest_emails: arr });
                   }}
                   data-testid={`booking-guest-${i}-input`} />
          ))}
        </div>
      </div>
      <div className="md:col-span-1 flex items-end">
        <Button className="w-full" disabled={busy || !form.plot_id} data-testid="booking-submit-btn">{busy ? "Requesting…" : "Request"}</Button>
      </div>
      {form.plot_id && (availability?.ranges?.length > 0 || availability?.maintenance_blocked?.length > 0) && (
        <div className="md:col-span-5 rounded-md border border-dashed border-destructive/40 bg-destructive/5 p-3 text-xs" data-testid="availability-hint">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="uppercase tracking-widest text-[10px] font-semibold text-destructive">Availability</span>
            {availability.ranges.length > 0 && (
              <span className="text-muted-foreground" data-testid="availability-hint-blocked">
                {availability.ranges.length} existing booking{availability.ranges.length === 1 ? "" : "s"} on this cottage — blocked nights are struck through in the calendar.
              </span>
            )}
            {availability.maintenance_blocked.length > 0 && (
              <span className="text-destructive font-semibold" data-testid="availability-hint-maintenance">
                ⚠ {availability.maintenance_blocked.length} high-priority maintenance task{availability.maintenance_blocked.length === 1 ? "" : "s"} active — new bookings will be rejected until resolved.
              </span>
            )}
            <div className="flex-1" />
            {availability.ranges.length > 0 && !admin && (
              <Button type="button" size="sm" variant="outline" onClick={onOpenWaitlist}
                      className="h-7 px-3 text-xs" data-testid="join-waitlist-btn">
                <BellRing size={12} className="mr-1.5" /> Join waitlist
              </Button>
            )}
          </div>
        </div>
      )}
      {(preview || previewErr) && (
        <div className="md:col-span-5 rounded-md border border-border p-4 bg-secondary/40" data-testid="rate-preview">
          {previewErr && <div className="text-xs text-destructive" data-testid="rate-preview-error">{previewErr}</div>}
          {preview && (
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2 text-sm">
              <div className="heading text-xl" data-testid="rate-preview-total">₹{Math.round(preview.total_price).toLocaleString("en-IN")}</div>
              {preview.discount_amount > 0 && (
                <>
                  <div className="text-muted-foreground line-through" data-testid="rate-preview-base">₹{Math.round(preview.base_price).toLocaleString("en-IN")}</div>
                  <div className="text-primary font-semibold" data-testid="rate-preview-sister">−{preview.sister_discount_percent}% sister</div>
                </>
              )}
              <div className="text-xs text-muted-foreground" data-testid="rate-preview-nights">{preview.nights} night{preview.nights === 1 ? "" : "s"}</div>
              {preview.rate_breakdown?.some((n) => n.rule) && (
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground" data-testid="rate-preview-rules">
                  {[...new Set(preview.rate_breakdown.filter((n) => n.rule).map((n) => n.rule))].join(" · ")}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </form>
  );
}
